// Подключение почты установки (008: spec FR-001 – FR-013, FR-031, FR-032; data-model.md,
// «Подключение почты», «Секрет подключения»; contracts/api.md, «Схемы»). Секреты наружу
// не отдаются: только последние 4 знака, кто и когда задал (FR-008).
import { z } from '../kernel/zod.ts';
import { DateSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';

/** Почтовые платформы среза 008; SMTP-платформы — 008-2. */
export const mailPlatforms = ['resend'] as const;
export type MailPlatform = (typeof mailPlatforms)[number];
export const MailPlatformSchema = z.enum(mailPlatforms);

/** Способ отправки: по API сервиса; 008-2 добавит smtp. */
export const mailMethods = ['api'] as const;
export const MailMethodSchema = z.enum(mailMethods);
export type MailMethod = z.infer<typeof MailMethodSchema>;

/** Способ отправки словами: «по API Resend» (FR-001); SMTP — следующий срез. */
export const mailMethodLabels = {
  api: 'по API',
} as const satisfies Record<MailMethod, string>;

/**
 * Выбор почтовой платформы в разделе: доступные и «в следующем срезе». Список — данные,
 * новая платформа — строка (FR-033 008-2).
 */
export const mailPlatformCatalog = [
  { id: 'resend', label: 'Resend', available: true, note: null },
  {
    id: 'exchange',
    label: 'Exchange на своём сервере',
    available: false,
    note: 'в следующем срезе',
  },
  { id: 'yandex360', label: 'Яндекс 360', available: false, note: 'в следующем срезе' },
  { id: 'vk_workspace', label: 'VK WorkSpace', available: false, note: 'в следующем срезе' },
  { id: 'smtp', label: 'Другой сервер (SMTP)', available: false, note: 'в следующем срезе' },
] as const;

export const mailConnectionStates = ['not_configured', 'checking', 'working', 'failing'] as const;
export type MailConnectionState = (typeof mailConnectionStates)[number];
export const MailConnectionStateSchema = z.enum(mailConnectionStates);

export const mailConnectionStateLabels = {
  not_configured: 'Отправка писем не настроена',
  checking: 'Идёт проверка',
  working: 'Работает',
  failing: 'Ошибка',
} as const satisfies Record<MailConnectionState, string>;

/** Права ключа Resend по итогу проверки: полный доступ или «только отправка». */
export const mailKeyRights = ['full', 'sending'] as const;
export type MailKeyRights = (typeof mailKeyRights)[number];

export const mailKeyRightsLabels = {
  full: 'полный доступ',
  sending: 'только отправка',
} as const satisfies Record<MailKeyRights, string>;

/** Регистрация отчётов о доставке у платформы (FR-024). */
export const mailReportsStates = ['none', 'pending', 'registered', 'failed'] as const;
export type MailReportsState = (typeof mailReportsStates)[number];

/** Возможность подключения «данные вне России» (FR-029): неизвестно — как «да». */
export const outsideRussiaValues = ['yes', 'no', 'unknown'] as const;
export type OutsideRussia = (typeof outsideRussiaValues)[number];

/**
 * Отметка согласия заказчика на полные письма через канал вне России (042: FR-030, research Р5;
 * поправка IV 3.1.0): дата согласия, кто и когда поставил отметку. Нет — уходит извещение.
 */
export const MailFullConsentSchema = z.object({
  on: DateSchema,
  setBy: PersonRefSchema.nullable(),
  setAt: TimestampSchema,
});
export type MailFullConsent = z.infer<typeof MailFullConsentSchema>;

export const secretSources = ['admin', 'platform'] as const;

/** Секрет — только последний фрагмент, кто и когда; секрет платформы — без автора. */
export const SecretBriefSchema = z.object({
  tail: z.string().length(4),
  setAt: TimestampSchema,
  setBy: PersonRefSchema.nullable(),
  source: z.enum(secretSources),
});
export type SecretBrief = z.infer<typeof SecretBriefSchema>;

/** Причина состояния: код MAIL-0xx из каталога ошибок, причина и шаг (принцип V). */
export const MailReasonSchema = z.object({
  code: z.string(),
  message: z.string(),
  nextStep: z.string(),
});
export type MailReason = z.infer<typeof MailReasonSchema>;

export const MailPlatformOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  available: z.boolean(),
  note: z.string().nullable(),
});

export const MailConnectionViewSchema = z.object({
  /** Сохранены платформа, отправитель и ключ. */
  configured: z.boolean(),
  platform: MailPlatformSchema.nullable(),
  method: MailMethodSchema.nullable(),
  sender: z.object({ name: z.string(), address: z.string() }).nullable(),
  replyTo: z.string().nullable(),
  signature: z.string(),
  contact: z.string(),
  key: SecretBriefSchema.nullable(),
  keyRights: z.enum(mailKeyRights).nullable(),
  domain: z
    .object({
      name: z.string(),
      verified: z.boolean(),
      receiving: z.boolean().nullable(),
      trackingOff: z.boolean().nullable(),
    })
    .nullable(),
  reports: z.object({
    state: z.enum(mailReportsStates),
    secret: SecretBriefSchema.nullable(),
    /** Адрес приёма отчётов: https://<домен установки>/hooks/resend. */
    url: z.string(),
  }),
  sendingEnabled: z.boolean(),
  /** 042: данные вне России у платформы подключения; null — подключения ещё нет. */
  outsideRussia: z.enum(outsideRussiaValues).nullable(),
  /** 042 (FR-030): отметка согласия на полные письма; null — согласия нет. */
  fullConsent: MailFullConsentSchema.nullable(),
  state: MailConnectionStateSchema,
  reason: MailReasonSchema.nullable(),
  checkedAt: TimestampSchema.nullable(),
  lastSentAt: TimestampSchema.nullable(),
  platforms: z.array(MailPlatformOptionSchema),
  /** 0 — подключение ещё не сохраняли: первое сохранение идёт с If-Match: 0. */
  version: z.number().int().min(0),
});
export type MailConnectionView = z.infer<typeof MailConnectionViewSchema>;

/** Канал почты на панели состояния (FR-032; research, Р10). */
// 033 (FR-009): stopped — «Остановить все сообщения», выше всех прочих состояний.
export const mailChannelStates = [
  'not_configured',
  'working',
  'failing',
  'disabled',
  'stopped',
] as const;
export type MailChannelState = (typeof mailChannelStates)[number];

export const mailChannelStateLabels = {
  not_configured: 'Не настроен',
  working: 'Работает',
  failing: 'Ошибка',
  disabled: 'Отправка отключена',
  stopped: 'Сообщения остановлены',
} as const satisfies Record<MailChannelState, string>;

export const MailChannelStatusSchema = z.object({
  state: z.enum(mailChannelStates),
  lastSentAt: TimestampSchema.nullable(),
  failingSince: TimestampSchema.nullable(),
  reason: z.object({ code: z.string() }).nullable(),
});
export type MailChannelStatus = z.infer<typeof MailChannelStatusSchema>;

/**
 * Раздел delivery файла установки (008: research, Р3, Р6): адрес API Resend (тесты — свой
 * сервер), предел ответа и промежуток между отправками — не больше 5 писем в секунду.
 */
export const DeliverySettingsSchema = z.strictObject({
  resend: z.strictObject({
    apiUrl: z.url({ protocol: /^https?$/ }).default('https://api.resend.com'),
    timeoutMs: z.number().int().min(1000).max(60_000).default(20_000),
    minIntervalMs: z.number().int().min(0).max(5000).default(200),
  }),
});
export type DeliverySettings = z.infer<typeof DeliverySettingsSchema>;
